import {
  useAddJobTask,
  useRemoveJobTask,
  useSetJobTaskMandatory,
} from '@/domains/jobs/hooks/useJobDetail';
import { useTaskTypes } from '@/domains/settings';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { useCan } from '@skewb/access-control/react';
import { ClipboardList, Lock, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

interface Task {
  id: number;
  name: string;
  // Raw `job_task.status` id (JOB_TASK scheme). The mobile task-form submit path
  // rolls task completion onto this column (backend jobTaskForm/add.ts).
  status: number | null;
  orderNumber?: number;
  isCategoryTask?: boolean;
  // Effective mandatory state: this job's override → job category default → true.
  // Optional tasks don't block job completion on mobile.
  isMandatory?: boolean;
  // True when this job overrides the category default.
  isMandatoryOverridden?: boolean;
  // True when the job category marks this task mandatory — read-only here.
  isMandatoryLocked?: boolean;
}

// JOB_TASK status ids (see backend utils/constant.ts STATUS_DB.JOB_TASK).
// Note: works-lite-seeded tasks use 1 for Not Started, other sources use 5.
const JOB_TASK_STATUS = {
  NOT_STARTED_WORKS_LITE: 1,
  NOT_STARTED: 5,
  ABORT: 8,
  IN_PROGRESS: 9,
  COMPLETED: 10,
} as const;

function getStatusLabel(status: number | null): string {
  switch (status) {
    case JOB_TASK_STATUS.COMPLETED:
      return 'Completed';
    case JOB_TASK_STATUS.IN_PROGRESS:
      return 'In Progress';
    case JOB_TASK_STATUS.ABORT:
      return 'Aborted';
    default:
      return 'Not Started';
  }
}

function getStatusStyle(status: number | null): string {
  const label = getStatusLabel(status);
  if (label === 'Completed')
    return 'bg-green-50 text-green-700 border-green-600 dark:bg-green-950/30';
  if (label === 'In Progress')
    return 'bg-blue-50 text-blue-700 border-blue-600 dark:bg-blue-950/30';
  if (label === 'Aborted') return 'bg-red-50 text-red-700 border-red-600 dark:bg-red-950/30';
  return 'bg-slate-50 text-slate-700 border-slate-400 dark:bg-slate-900/30';
}

function getStatusDot(status: number | null): string {
  const label = getStatusLabel(status);
  if (label === 'Completed') return 'bg-green-600';
  if (label === 'In Progress') return 'bg-blue-600';
  if (label === 'Aborted') return 'bg-red-600';
  return 'bg-slate-400';
}

export function TasksCard({
  tasks,
  jobId,
  taskAddBlocked,
}: {
  tasks?: Task[];
  jobId?: string;
  // CWL-322: once the job is en route (or beyond), mobile has already
  // started it — no new task can be added.
  taskAddBlocked?: boolean;
}) {
  const allTasks = tasks && tasks.length > 0 ? tasks : [];
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [taskSearch, setTaskSearch] = useState('');
  const [removeConfirm, setRemoveConfirm] = useState<Task | null>(null);

  const numericJobId = Number(jobId);
  const addTaskMutation = useAddJobTask(numericJobId);
  const removeTaskMutation = useRemoveJobTask(numericJobId);
  const setMandatoryMutation = useSetJobTaskMandatory(numericJobId);
  // WRK-5670: task controls mirror the backend job-task guards — add needs read TaskType (the
  // picker list) + update Job, mandatory needs update Job, remove needs delete Job.
  const canReadTaskTypes = useCan('read', 'TaskType');
  const canUpdateJob = useCan('update', 'Job');
  const canAddTask = canReadTaskTypes && canUpdateJob;
  const canRemoveTask = useCan('delete', 'Job');
  const { data: taskTypesData } = useTaskTypes({ enabled: canReadTaskTypes });
  const allTaskTypes = taskTypesData?.data ?? [];

  const existingTaskNames = useMemo(
    () => new Set(allTasks.map((t) => t.name.toLowerCase())),
    [allTasks],
  );

  const availableTaskTypes = useMemo(() => {
    // CWL-322: a task type with no form mapped can never be completed on
    // mobile, which would permanently block the job from reaching Completed
    // (and syncing to SAP) — so only offer task types that have a form.
    // CWL-499: a repeatable task type (e.g. DR4) stays on offer once it is on the
    // job, because each add is another instance.
    let pool = allTaskTypes.filter(
      (tt) =>
        tt.hasMappedForms &&
        (tt.canRepeat === true || !existingTaskNames.has((tt.name ?? '').toLowerCase())),
    );
    if (taskSearch) {
      const q = taskSearch.toLowerCase();
      pool = pool.filter((tt) => tt.name?.toLowerCase().includes(q));
    }
    return pool;
  }, [allTaskTypes, existingTaskNames, taskSearch]);

  async function handleAddTask(taskTypeId: number) {
    const nextOrder =
      allTasks.length > 0 ? Math.max(...allTasks.map((t) => t.orderNumber ?? 0)) + 1 : 1;
    try {
      await addTaskMutation.mutateAsync({ taskTypeId, orderNumber: nextOrder });
      showSuccessToast('Task added');
      setAddDialogOpen(false);
      setTaskSearch('');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? err?.message ?? 'Failed to add task';
      showErrorToast(msg);
    }
  }

  async function handleToggleMandatory(task: Task, isMandatory: boolean) {
    try {
      await setMandatoryMutation.mutateAsync({ taskId: task.id, isMandatory });
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? err?.message ?? 'Failed to update task';
      showErrorToast(msg);
    }
  }

  async function handleRemoveTask() {
    if (!removeConfirm) return;
    try {
      await removeTaskMutation.mutateAsync(removeConfirm.id);
      showSuccessToast('Task removed');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? err?.message ?? 'Failed to remove task';
      showErrorToast(msg);
    }
    setRemoveConfirm(null);
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Tasks</h3>
            <p className="text-xs text-muted-foreground">
              {allTasks.length > 0
                ? `${allTasks.length} task${allTasks.length !== 1 ? 's' : ''}`
                : 'No tasks assigned'}
            </p>
          </div>
        </div>
        {jobId && canAddTask && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={taskAddBlocked}
                  onClick={() => {
                    setTaskSearch('');
                    setAddDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Task
                </Button>
              </span>
            </TooltipTrigger>
            {taskAddBlocked && (
              <TooltipContent>Cannot add tasks once the engineer has gone en route.</TooltipContent>
            )}
          </Tooltip>
        )}
      </div>

      {allTasks.length > 0 && (
        <div className="space-y-2">
          {allTasks.map((task: Task) => (
            <div
              key={task.id}
              className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border hover:bg-muted/70 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <span className="text-xs font-medium text-muted-foreground w-6 text-center">
                  {task.orderNumber ?? '-'}
                </span>
                <div className={`w-2 h-2 rounded-full ${getStatusDot(task.status)}`} />
                <span className="text-sm font-medium text-foreground flex-1">{task.name}</span>
              </div>
              <div className="flex items-center gap-2 ml-3">
                {/* Per-job requirement override. Mandatory tasks must be completed
                    before the operative can finish the job on mobile; optional
                    ones can be skipped.

                    Only tasks the job category marks OPTIONAL can be changed here
                    — a category-mandatory task is read-only and must be relaxed in
                    Settings → Category Tasks, so one job can't quietly drop a
                    requirement the category insists on.

                    Without update Job the checkbox is read-only too, and says why. */}
                <label
                  className={`flex items-center gap-1.5 ${
                    task.isMandatoryLocked || !canUpdateJob ? 'cursor-default' : 'cursor-pointer'
                  }`}
                  title={
                    task.isMandatoryLocked
                      ? 'Mandatory for this job category — change it in Settings → Category Tasks'
                      : !canUpdateJob
                        ? "You don't have permission to change whether tasks are mandatory on this job"
                        : undefined
                  }
                >
                  <Checkbox
                    checked={task.isMandatory ?? true}
                    disabled={
                      !canUpdateJob || task.isMandatoryLocked || setMandatoryMutation.isPending
                    }
                    onCheckedChange={(checked) => handleToggleMandatory(task, checked === true)}
                    aria-label={
                      task.isMandatoryLocked
                        ? `${task.name} is mandatory for this job category and cannot be changed here`
                        : !canUpdateJob
                          ? `You don't have permission to change whether ${task.name} is mandatory on this job`
                          : `Mark ${task.name} as mandatory on this job`
                    }
                  />
                  <span className="text-xs text-muted-foreground">
                    {(task.isMandatory ?? true) ? 'Mandatory' : 'Optional'}
                  </span>
                </label>

                <Badge variant="outline" className={getStatusStyle(task.status)}>
                  {getStatusLabel(task.status)}
                </Badge>

                {task.isCategoryTask ? (
                  <span className="relative group flex items-center justify-center h-7 w-7">
                    <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="absolute bottom-full right-0 mb-1.5 px-2.5 py-1.5 text-xs text-white bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 rounded-md shadow-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
                      Assigned from job category — cannot be removed here
                    </span>
                  </span>
                ) : canRemoveTask ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => setRemoveConfirm(task)}
                    title="Remove task"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Task Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search task types..."
                value={taskSearch}
                onChange={(e) => setTaskSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
              {availableTaskTypes.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground text-center">
                  {taskSearch
                    ? 'No task types match your search'
                    : 'All task types already assigned'}
                </div>
              ) : (
                availableTaskTypes.map((tt) => (
                  <button
                    key={tt.id}
                    type="button"
                    onClick={() => handleAddTask(tt.id)}
                    disabled={addTaskMutation.isPending}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 text-left text-sm border-b last:border-b-0 transition-colors"
                  >
                    <ClipboardList className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-foreground">{tt.name}</span>
                  </button>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog open={!!removeConfirm} onOpenChange={() => setRemoveConfirm(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Remove Task</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to remove{' '}
            <span className="font-medium text-foreground">{removeConfirm?.name}</span> from this
            job?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveTask}
              disabled={removeTaskMutation.isPending}
            >
              {removeTaskMutation.isPending ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
